// src/components/CredentialManagement/CredentialCard.js
import React from "react";
import PropTypes from "prop-types";

/**
 * CredentialCard - Individual credential display card
 *
 * Displays a single credential with patron info, Wiegand data, and status.
 * Handles pending deletion and offline states with visual overlays.
 */
const TRANSPORT_TECHNOLOGY_LABELS = {
  ANY:               "Any",
  PROX:              "Prox Card",
  UID:               "Smart Credential (UID)",
  MIFARE_CLASSIC:    "MiFare Classic",
  MIFARE_DESFIRE:    "MiFare DesFire",
  APPLE_NFC:         "Apple NFC Phone/Watch",
  ANDROID_MIFARE2GO: "Android NFC Phone/Wearable",
  ALLEGION_BLE:      "Allegion BLE",
};

const CredentialCard = ({
  credential,
  patron,
  isPendingDeletion,
  isOffline,
  deviceHasPendingDeletions,
  onDelete,
}) => {
  return (
    <div
      className={`credential-card ${
        credential.status?.toLowerCase() || "active"
      } ${
        isPendingDeletion
          ? isOffline
            ? "device-offline"
            : "pending-deletion"
          : ""
      }`}
      style={{ position: "relative" }}
    >
      {isPendingDeletion && (
        <div
          className={
            isOffline
              ? "credential-offline-overlay"
              : "credential-deletion-overlay"
          }
        >
          <div
            className={
              isOffline
                ? "credential-offline-message"
                : "credential-deletion-message"
            }
          >
            <strong>
              {isOffline
                ? "Can't Delete; Device is Offline"
                : "Pending Deletion..."}
            </strong>
          </div>
        </div>
      )}

      <div className="credential-card-header">
        <h3>Credential</h3>
        <button
          className="credential-delete-btn"
          onClick={(e) => {
            if (deviceHasPendingDeletions) {
              const button = e.currentTarget;
              button.focus();
              setTimeout(() => {
                if (button) {
                  button.blur();
                }
              }, 2000);
            } else {
              onDelete(credential.id, patron);
            }
          }}
          title={
            deviceHasPendingDeletions
              ? "Please wait for current deletion to complete"
              : "Remove credential from this device only"
          }
          aria-label="Delete credential from device"
          style={{
            cursor: deviceHasPendingDeletions ? "not-allowed" : "pointer",
            opacity: deviceHasPendingDeletions ? 0.6 : 1,
          }}
        >
          {deviceHasPendingDeletions ? "⏳" : "🗑️"}
        </button>
      </div>

      {patron?.badgePhotoUrl && (
        <div className="cred-photo-container">
          <img
            src={patron.badgePhotoUrl}
            alt="Badge"
            className="cred-badge-photo"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}

      {patron && (patron.firstName || patron.lastName) && (
        <p className="cred-user">
          <strong>User:</strong> {patron.firstName} {patron.lastName}
          {patron.email && ` (${patron.email})`}
        </p>
      )}

      {credential.credentialType === "PHYSICAL" || credential.deviceType === "PHYSICAL" ? (
        <p className="cred-device-type">
          <strong>Type:</strong>{" "}
          {TRANSPORT_TECHNOLOGY_LABELS[credential.transportTechnology] ?? "Physical Credential"}
        </p>
      ) : credential.deviceType ? (
        <p className="cred-device-type">
          <strong>Type:</strong> {credential.deviceType.replace("_", " ")}
        </p>
      ) : null}

      {(credential.facilityCode ||
        credential.badgeId ||
        credential.bitFormat) && (
        <div className="cred-wiegand-info">
          {credential.facilityCode && (
            <span className="cred-wiegand-field">
              <strong>Facility:</strong> {credential.facilityCode}
            </span>
          )}
          {credential.badgeId && (
            <span className="cred-wiegand-field">
              <strong>Badge:</strong> {credential.badgeId}
            </span>
          )}
          {credential.bitFormat && (
            <span className="cred-wiegand-field">
              <strong>Format:</strong> {credential.bitFormat}
            </span>
          )}
        </div>
      )}

      <p className="cred-id">
        <strong>GUID:</strong> {credential.id}
      </p>

      <p className="cred-value">
        <strong>Prime CR:</strong> {credential.credential}
      </p>

      <div className="cred-status">
        <span>Status:</span> <strong>{credential.status || "Active"}</strong>
      </div>
    </div>
  );
};

CredentialCard.propTypes = {
  credential: PropTypes.object.isRequired,
  patron: PropTypes.object,
  isPendingDeletion: PropTypes.bool.isRequired,
  isOffline: PropTypes.bool.isRequired,
  deviceHasPendingDeletions: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CredentialCard;
